import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./css/dilemmaGame.css";
import useGameState from "../hooks/useGameState";
import useTranslation from "../hooks/useTranslation";

// Recursos
import edificos from "../assets/recursosJuego/EDIFICIOS-01.png";
import gameLogo from "../assets/recursosJuego/greenescape-logo.png";

// Importar todas las imágenes en WebP (más ligeras)
import imagen1 from "../assets/recursosJuego/1.webp";
import imagen2 from "../assets/recursosJuego/2.webp";
import imagen3 from "../assets/recursosJuego/3.webp";
import imagen4 from "../assets/recursosJuego/4.webp";
import imagen5 from "../assets/recursosJuego/5.webp";
import imagen6 from "../assets/recursosJuego/6.webp";
import imagen7 from "../assets/recursosJuego/7.webp";
import imagen8 from "../assets/recursosJuego/8.webp";
import imagen9 from "../assets/recursosJuego/9.webp";
import imagen10 from "../assets/recursosJuego/10.webp";
import imagen11 from "../assets/recursosJuego/11.webp";
import imagen12 from "../assets/recursosJuego/12.webp";
import imagen13 from "../assets/recursosJuego/13.webp";
import imagen14 from "../assets/recursosJuego/14.webp";
import imagen15 from "../assets/recursosJuego/15.webp";
import imagen16 from "../assets/recursosJuego/16.webp";
import imagen17 from "../assets/recursosJuego/17.webp";
import imagen18 from "../assets/recursosJuego/18.webp";

// Array de todas las imágenes disponibles
const heroImages = [
  imagen1, imagen2, imagen3, imagen4, imagen5, imagen6, imagen7, imagen8, imagen9,
  imagen10, imagen11, imagen12, imagen13, imagen14, imagen15, imagen16, imagen17, imagen18
];

function DilemmaGame({ updateProgress }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCharacters } = useGameState();
  const players = selectedCharacters?.playersArray || [];
  
  // Función helper para obtener el nombre traducido del personaje
  const getCharacterName = (characterId) => {
    return t(`characterSelection.characters.${characterId}.name`);
  };
  
  // Los 10 riesgos de Awareness (para mapear)
  const allRisks = t('awarenessWrapup.risks');
  
  // Cargar los 3 riesgos seleccionados del localStorage
  const [selectedRisks, setSelectedRisks] = useState([]);
  
  // Estado para manejar turnos, respuestas y riesgos
  const [currentRiskIndex, setCurrentRiskIndex] = useState(0);
  const [currentTurnIndex, setCurrentTurnIndex] = useState(0);
  const [playerAnswers, setPlayerAnswers] = useState({}); // { riskId: { playerId: "respuesta escrita" } }
  const [currentAnswer, setCurrentAnswer] = useState(""); // Respuesta actual del textarea
  const [showProposals, setShowProposals] = useState(false); // Mostrar la fase de propuestas
  const [showVoting, setShowVoting] = useState(false); // Mostrar la fase de votación
  const [proposal1, setProposal1] = useState(""); // Propuesta 1
  const [proposal2, setProposal2] = useState(""); // Propuesta 2
  const [proposalVotes, setProposalVotes] = useState({}); // { riskId: { playerId: 1 o 2 } }
  const [currentVoteTurnIndex, setCurrentVoteTurnIndex] = useState(0); // Turno actual para votar
  const [votingResults, setVotingResults] = useState(null); // Resultados de la votación { proposal1: count, proposal2: count }
  const [usedImages, setUsedImages] = useState([]); // Imágenes ya usadas
  const [currentHeroImage, setCurrentHeroImage] = useState(() => {
    // Seleccionar imagen aleatoria al inicio
    const randomIndex = Math.floor(Math.random() * heroImages.length);
    return heroImages[randomIndex];
  });

  // Drawer de respuestas (móvil/tablet)
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Debug: Log cuando cambian los estados importantes
  useEffect(() => {
    console.log("=== STATE UPDATE ===");
    console.log("showProposals:", showProposals);
    console.log("showVoting:", showVoting);
    console.log("currentRiskIndex:", currentRiskIndex);
    console.log("proposal1:", proposal1);
    console.log("proposal2:", proposal2);
    console.log("votingResults:", votingResults);
  }, [showProposals, showVoting, currentRiskIndex, proposal1, proposal2, votingResults]);

  // Cargar riesgos seleccionados al montar
  useEffect(() => {
    const riskIndices = JSON.parse(localStorage.getItem('greenscape_selected_dilemma_risks') || '[]');
    const risks = riskIndices.map((index, id) => ({
      id: id + 1,
      index,
      title: t('dilemmaGame.riskTitle', { number: id + 1 }),
      text: allRisks[index]
    }));
    
    // Añadir el 4to desafío FIJO de Justicia Climática
    risks.push({
      id: 4,
      index: 'climate-justice',
      title: t('dilemmaGame.climateJusticeTitle'),
      text: t('dilemmaGame.climateJusticeText')
    });
    
    console.log("=== DILEMMA GAME - RISKS LOADED ===");
    console.log("Total risks:", risks.length);
    console.log("Risks:", risks.map(r => r.title));
    
    setSelectedRisks(risks);
  }, []);

  // Función para obtener la siguiente imagen sin repetir recientes
  const getNextImage = () => {
    let availableImages = heroImages.filter(img => !usedImages.includes(img));
    
    // Si ya usamos todas las imágenes, reiniciar pero mantener la actual fuera
    if (availableImages.length === 0) {
      availableImages = heroImages.filter(img => img !== currentHeroImage);
      setUsedImages([]);
    }
    
    // Seleccionar aleatoriamente de las disponibles
    const randomIndex = Math.floor(Math.random() * availableImages.length);
    const newImage = availableImages[randomIndex];
    
    // Agregar a las usadas
    setUsedImages(prev => [...prev, newImage]);
    
    return newImage;
  };

  const currentRisk = selectedRisks[currentRiskIndex];
  const currentPlayer = players[currentTurnIndex];
  const currentVotingPlayer = players[currentVoteTurnIndex];
  
  // Obtener respuestas del riesgo actual
  const currentRiskAnswers = currentRisk ? (playerAnswers[currentRisk.id] || {}) : {};
  const allPlayersAnswered = Object.keys(currentRiskAnswers).length === players.length;
  
  // Obtener votos del riesgo actual
  const currentRiskVotes = currentRisk ? (proposalVotes[currentRisk.id] || {}) : {};
  const allPlayersVoted = Object.keys(currentRiskVotes).length === players.length;

  // Verificar si hay riesgos cargados
  if (selectedRisks.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        fontSize: '1.5rem',
        background: '#1e6a3a'
      }}>
        {t('dilemmaGame.loadingMessage')}
      </div>
    );
  }

  // Función para manejar el envío de la respuesta
  const handleSubmitAnswer = () => {
    if (!currentAnswer.trim()) {
      console.log("La respuesta no puede estar vacía");
      return;
    }

    // Guardar la respuesta del jugador actual
    setPlayerAnswers((prev) => ({
      ...prev,
      [currentRisk.id]: {
        ...prev[currentRisk.id],
        [currentPlayer.characterId]: currentAnswer,
      },
    }));

    // Limpiar el textarea
    setCurrentAnswer("");

    // Avanzar al siguiente turno
    if (currentTurnIndex < players.length - 1) {
      setCurrentTurnIndex(currentTurnIndex + 1);
    } else {
      // Todos han respondido, mostrar la fase de propuestas
      setShowProposals(true);
    }
  };

  // Función para enviar las propuestas y activar la fase de votación
  const handleSubmitProposals = () => {
    if (!proposal1.trim() || !proposal2.trim()) {
      alert(t('dilemmaGame.alertCompleteProposals'));
      return;
    }

    console.log("=== SUBMIT PROPOSALS ===");
    console.log("Proposal1:", proposal1);
    console.log("Proposal2:", proposal2);
    console.log("Activating voting phase...");

    // Activar la fase de votación dentro de proposals
    setShowVoting(true);
    setCurrentVoteTurnIndex(0);
    setProposalVotes({});
    setVotingResults(null);
    
    console.log("Voting phase activated");
  };

  // Función para manejar el voto de un jugador
  const handleVote = (proposalNumber) => {
    console.log("=== HANDLE VOTE ===");
    console.log("proposalNumber:", proposalNumber);
    console.log("currentVoteTurnIndex:", currentVoteTurnIndex);
    console.log("players.length:", players.length);
    
    const currentVotingPlayer = players[currentVoteTurnIndex];
    console.log("currentVotingPlayer:", currentVotingPlayer);
    
    if (!currentVotingPlayer) {
      console.error("No current voting player!");
      return;
    }
    
    // Crear el nuevo estado de votos con el voto actual
    const updatedVotes = {
      ...proposalVotes,
      [currentRisk.id]: {
        ...proposalVotes[currentRisk.id],
        [currentVotingPlayer.characterId]: proposalNumber,
      },
    };
    
    console.log("updatedVotes:", updatedVotes);
    
    // Guardar el voto
    setProposalVotes(updatedVotes);

    // Avanzar al siguiente turno de votación
    if (currentVoteTurnIndex < players.length - 1) {
      console.log("Moving to next voting turn");
      setCurrentVoteTurnIndex(currentVoteTurnIndex + 1);
    } else {
      console.log("All players have voted, calculating results");
      // Todos han votado, calcular resultados
      const riskVotes = updatedVotes[currentRisk.id] || {};
      const count1 = Object.values(riskVotes).filter(v => v === 1).length;
      const count2 = Object.values(riskVotes).filter(v => v === 2).length;
      
      console.log(`Voting results: Proposal 1: ${count1}, Proposal 2: ${count2}`);
      
      // Verificar si hay empate
      if (count1 === count2) {
        console.log("TIE DETECTED! Restarting voting...");
        // Hay empate, mostrar resultados temporalmente y reiniciar votación
        setVotingResults({ proposal1: count1, proposal2: count2 });
        
        // Esperar 2 segundos para mostrar el empate y luego reiniciar
        setTimeout(() => {
          console.log("Restarting voting due to tie");
          // Primero limpiar resultados
          setVotingResults(null);
          
          // Luego limpiar los votos del riesgo actual
          const clearedVotes = { ...proposalVotes };
          delete clearedVotes[currentRisk.id];
          setProposalVotes(clearedVotes);
          
          // Finalmente reiniciar el índice de votación al primer jugador
          setCurrentVoteTurnIndex(0);
        }, 2000);
      } else {
        // Hay un ganador
        setVotingResults({ proposal1: count1, proposal2: count2 });
        
        // Esperar 3 segundos antes de continuar
        setTimeout(() => {
          // Guardar las propuestas y votos en localStorage
          const proposalsData = JSON.parse(localStorage.getItem('greenscape_dilemma_proposals') || '{}');
          proposalsData[currentRisk.id] = {
            risk: currentRisk.text,
            proposal1,
            proposal2,
            playerAnswers: currentRiskAnswers,
            votes: riskVotes,
            votingResults: { proposal1: count1, proposal2: count2 }
          };
          localStorage.setItem('greenscape_dilemma_proposals', JSON.stringify(proposalsData));

          // Pasar al siguiente riesgo o terminar
          if (currentRiskIndex < selectedRisks.length - 1) {
            console.log("→ Moving to NEXT risk");
            // Primero resetear todos los estados
            setShowVoting(false);
            setShowProposals(false);
            setVotingResults(null);
            setProposalVotes({});
            setDrawerOpen(false);
            setCurrentVoteTurnIndex(0);
            // Luego cambiar de riesgo
            setCurrentRiskIndex(currentRiskIndex + 1);
            setCurrentTurnIndex(0);
            setProposal1("");
            setProposal2("");
            // Cambiar imagen
            const newImage = getNextImage();
            setCurrentHeroImage(newImage);
          } else {
            console.log("→ FINISHING Dilemma phase - navigating to reflection");
            console.log("Calling updateProgress('dilema-game')");
            // Terminar fase Dilemma
            updateProgress('dilema-game');
            
            // Verificar que se guardó
            setTimeout(() => {
              const progress = JSON.parse(localStorage.getItem('greenscape_progress'));
              console.log("Progress after update:", progress);
              console.log("Navigating to /dilemma-reflection");
              navigate('/dilemma-reflection');
            }, 100);
          }
        }, 3000);
      }
    }
  };

  return (
    <div className="dilema-game-wrapper">
      <div className={`dilema-game-layout ${showProposals ? 'show-proposals' : ''}`}>
        {/* Sidebar izquierda */}
        <aside className="game-sidebar">
          <div className="fraction">
            <div className="big">{currentRisk.id}</div>
            <div className="small">/ {selectedRisks.length}</div>
          </div>
          <div className="mode">{t('dilemmaGame.mode')}</div>
          <ul className="turn-list">
            {players.map((p, idx) => {
              const isActiveInAnswers = idx === currentTurnIndex && !showProposals && !showVoting;
              const isActiveInVoting = idx === currentVoteTurnIndex && showVoting;
              const isActive = isActiveInAnswers || isActiveInVoting;
              const showAllActive = showProposals || (showVoting && allPlayersVoted);
              
              return (
                <li
                  key={p.characterId}
                  className={`turn-item ${isActive ? "active" : ""} ${showAllActive ? "all-active" : ""}`}
                >
                  <div className="turn-item-content">
                    <img className="turn-avatar" src={p.image} alt={getCharacterName(p.characterId)} />
                    <div className="turn-text-container">
                      {(isActive || showAllActive) && (
                        <div className="turn-label">{t('dilemmaGame.yourTurnLabel')}</div>
                      )}
                      <span className="turn-name">{getCharacterName(p.characterId)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Zona principal */}
        <main className="game-main">
          <div className="hero">
            <img src={currentHeroImage} alt="Hero" className="hero-img" />
            <img src={edificos} alt="Skyline" className="hero-skyline" />
            
            {/* Mostrar respuestas superpuestas cuando todos hayan respondido */}
            {showProposals && allPlayersAnswered && (
              <div className="answers-overlay">
                {Object.entries(currentRiskAnswers).map(([playerId, answer]) => {
                  const player = players.find(p => p.characterId === playerId);
                  return (
                    <div key={playerId} className="overlay-answer-card">
                      <div className="overlay-player-header">
                        <img src={player.image} alt={getCharacterName(player.characterId)} className="overlay-avatar" />
                        <strong>{getCharacterName(player.characterId)}:</strong>
                      </div>
                      <p className="overlay-answer-text">{answer}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Franja inferior de pregunta - ancho completo */}
      <section className="question-strip">
        <div className="qs-grid">
          <div className="qs-left">
            <img src={gameLogo} alt="GreenScape" className="qs-logo" />
          </div>
          <div className="qs-right">
            {!showProposals ? (
              <>
                <h2 className="q-title">{currentRisk.title}</h2>
                <p className="q-text">{currentRisk.text}</p>
                
                {/* Área de respuesta con textarea y botón a la derecha */}
                <div className="answer-area">
                  <textarea
                    className="answer-textarea"
                    placeholder={t('dilemmaGame.answerPlaceholder', { playerName: getCharacterName(currentPlayer?.characterId) })}
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    maxLength={300}
                    rows={4}
                  />
                  <button 
                    className="submit-answer-btn"
                    onClick={handleSubmitAnswer}
                    disabled={!currentAnswer.trim()}
                  >
                    {t('dilemmaGame.submitButton')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {!showVoting ? (
                  <>
                    <p className="q-text">{t('dilemmaGame.proposalsSubtitle')}</p>
                    {/* Propuestas consensuadas */}
                    <div className="proposals-area">
                      <div className="proposals-content">
                        <div className="proposal-wrapper">
                          <label className="proposal-label">{t('dilemmaGame.proposal1Label')}</label>
                          <textarea
                            className="proposal-textarea"
                            value={proposal1}
                            onChange={(e) => setProposal1(e.target.value)}
                            placeholder={t('dilemmaGame.proposal1Placeholder')}
                            maxLength={200}
                            rows={4}
                          />
                        </div>
                        
                        <div className="proposal-wrapper">
                          <label className="proposal-label">{t('dilemmaGame.proposal2Label')}</label>
                          <textarea
                            className="proposal-textarea"
                            value={proposal2}
                            onChange={(e) => setProposal2(e.target.value)}
                            placeholder={t('dilemmaGame.proposal2Placeholder')}
                            maxLength={200}
                            rows={4}
                          />
                        </div>
                        
                        {/* Botón para abrir drawer SOLO en móviles/tablets */}
                        {Object.keys(currentRiskAnswers).length > 0 && (
                          <button
                            type="button"
                            className="toggle-answers-btn"
                            onClick={() => setDrawerOpen(true)}
                          >
                            {t('dilemmaGame.viewAnswersButton', { count: Object.keys(currentRiskAnswers).length })}
                          </button>
                        )}
                      </div>

                      <button 
                        type="button"
                        className="submit-proposals-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Submit proposals button clicked");
                          handleSubmitProposals();
                        }}
                        disabled={!proposal1.trim() || !proposal2.trim()}
                      >
                        {t('dilemmaGame.submitProposalsButton')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="q-title">{t('dilemmaGame.votingTitle')}</h2>
                    <p className="q-text">
                      {(() => {
                        if (votingResults) {
                          const isTie = votingResults.proposal1 === votingResults.proposal2;
                          const params = {
                            proposal1Votes: votingResults.proposal1,
                            proposal2Votes: votingResults.proposal2
                          };
                          console.log("Voting message params:", params);
                          console.log("Is tie:", isTie);
                          
                          const message = isTie 
                            ? t('dilemmaGame.votingTieMessage', params)
                            : t('dilemmaGame.votingResults', params);
                          
                          console.log("Translated message:", message);
                          return message;
                        } else if (currentVotingPlayer) {
                          return t('dilemmaGame.votingSubtitle', { 
                            playerName: getCharacterName(currentVotingPlayer.characterId) 
                          });
                        }
                        return '';
                      })()}
                    </p>
                    
                    {!votingResults ? (
                      <div className="voting-options">
                        <button
                          className="vote-proposal-btn"
                          onClick={() => {
                            console.log("Vote button clicked for proposal 1");
                            handleVote(1);
                          }}
                          disabled={!currentVotingPlayer || (currentVotingPlayer && currentRiskVotes[currentVotingPlayer.characterId])}
                        >
                          <div className="vote-proposal-label">{t('dilemmaGame.proposal1Label')}</div>
                          <div className="vote-proposal-text">{proposal1}</div>
                          {/* Mostrar avatares de quienes ya votaron por esta propuesta */}
                          {Object.entries(currentRiskVotes)
                            .filter(([playerId, vote]) => vote === 1)
                            .map(([playerId]) => {
                              const player = players.find(p => p.characterId === playerId);
                              return player;
                            }).filter(p => p).length > 0 && (
                            <div className="vote-proposal-avatars">
                              {Object.entries(currentRiskVotes)
                                .filter(([playerId, vote]) => vote === 1)
                                .map(([playerId]) => {
                                  const player = players.find(p => p.characterId === playerId);
                                  return player ? (
                                    <img
                                      key={playerId}
                                      src={player.image}
                                      alt={getCharacterName(player.characterId)}
                                      className="vote-proposal-avatar"
                                      title={getCharacterName(player.characterId)}
                                    />
                                  ) : null;
                                })}
                            </div>
                          )}
                        </button>
                        
                        <button
                          className="vote-proposal-btn"
                          onClick={() => {
                            console.log("Vote button clicked for proposal 2");
                            handleVote(2);
                          }}
                          disabled={!currentVotingPlayer || (currentVotingPlayer && currentRiskVotes[currentVotingPlayer.characterId])}
                        >
                          <div className="vote-proposal-label">{t('dilemmaGame.proposal2Label')}</div>
                          <div className="vote-proposal-text">{proposal2}</div>
                          {/* Mostrar avatares de quienes ya votaron por esta propuesta */}
                          {Object.entries(currentRiskVotes)
                            .filter(([playerId, vote]) => vote === 2)
                            .map(([playerId]) => {
                              const player = players.find(p => p.characterId === playerId);
                              return player;
                            }).filter(p => p).length > 0 && (
                            <div className="vote-proposal-avatars">
                              {Object.entries(currentRiskVotes)
                                .filter(([playerId, vote]) => vote === 2)
                                .map(([playerId]) => {
                                  const player = players.find(p => p.characterId === playerId);
                                  return player ? (
                                    <img
                                      key={playerId}
                                      src={player.image}
                                      alt={getCharacterName(player.characterId)}
                                      className="vote-proposal-avatar"
                                      title={getCharacterName(player.characterId)}
                                    />
                                  ) : null;
                                })}
                            </div>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="voting-results">
                        <div className="voting-result-item">
                          <div className="voting-result-label">{t('dilemmaGame.proposal1Label')}</div>
                          <div className="voting-result-text">{proposal1}</div>
                          {/* Mostrar avatares de quienes votaron por esta propuesta */}
                          {Object.entries(currentRiskVotes)
                            .filter(([playerId, vote]) => vote === 1)
                            .map(([playerId]) => {
                              const player = players.find(p => p.characterId === playerId);
                              return player;
                            }).filter(p => p).length > 0 && (
                            <div className="voting-result-avatars">
                              {Object.entries(currentRiskVotes)
                                .filter(([playerId, vote]) => vote === 1)
                                .map(([playerId]) => {
                                  const player = players.find(p => p.characterId === playerId);
                                  return player ? (
                                    <img
                                      key={playerId}
                                      src={player.image}
                                      alt={getCharacterName(player.characterId)}
                                      className="voting-result-avatar"
                                      title={getCharacterName(player.characterId)}
                                    />
                                  ) : null;
                                })}
                            </div>
                          )}
                          <div className="voting-result-count">
                            {votingResults.proposal1} {votingResults.proposal1 === 1 ? t('dilemmaGame.vote') : t('dilemmaGame.votes')}
                          </div>
                        </div>
                        
                        <div className="voting-result-item">
                          <div className="voting-result-label">{t('dilemmaGame.proposal2Label')}</div>
                          <div className="voting-result-text">{proposal2}</div>
                          {/* Mostrar avatares de quienes votaron por esta propuesta */}
                          {Object.entries(currentRiskVotes)
                            .filter(([playerId, vote]) => vote === 2)
                            .map(([playerId]) => {
                              const player = players.find(p => p.characterId === playerId);
                              return player;
                            }).filter(p => p).length > 0 && (
                            <div className="voting-result-avatars">
                              {Object.entries(currentRiskVotes)
                                .filter(([playerId, vote]) => vote === 2)
                                .map(([playerId]) => {
                                  const player = players.find(p => p.characterId === playerId);
                                  return player ? (
                                    <img
                                      key={playerId}
                                      src={player.image}
                                      alt={getCharacterName(player.characterId)}
                                      className="voting-result-avatar"
                                      title={getCharacterName(player.characterId)}
                                    />
                                  ) : null;
                                })}
                            </div>
                          )}
                          <div className="voting-result-count">
                            {votingResults.proposal2} {votingResults.proposal2 === 1 ? t('dilemmaGame.vote') : t('dilemmaGame.votes')}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Drawer de respuestas (móvil/tablet) - solo visible en fase de propuestas (antes de votar) */}
      {showProposals && !showVoting && (
        <div className={`answers-drawer ${drawerOpen ? 'open' : ''}`}>
          <div className="answers-drawer-header">
            <strong>{t('dilemmaGame.drawerTitle')}</strong>
            <button
              type="button"
              className="close-answers-btn"
              onClick={() => setDrawerOpen(false)}
            >
              {t('dilemmaGame.drawerCloseButton')}
            </button>
          </div>
          <div className="answers-drawer-content">
            {Object.entries(currentRiskAnswers).map(([playerId, answer]) => {
              const player = players.find(p => p.characterId === playerId);
              return (
                <div key={playerId} className="overlay-answer-card">
                  <div className="overlay-player-header">
                    <img src={player.image} alt={getCharacterName(player.characterId)} className="overlay-avatar" />
                    <strong>{getCharacterName(player.characterId)}:</strong>
                  </div>
                  <p className="overlay-answer-text">{answer}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default DilemmaGame;
